import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getApiBase } from '../api/base.js';
import { platform } from '../platform.js';
/**
 * The single in-flight refresh.
 *
 * THIS IS LOAD-BEARING, and it is the whole reason `refresh` is not just a
 * fetch. The refresh token is single-use and rotated on every call, and the
 * server treats a replayed token as theft: it revokes the entire token family
 * (TAD 4.6 reuse detection). That control is correct and must not be relaxed.
 *
 * The client is what has to behave. When the 15-minute access token expires,
 * every request in flight gets a 401 at the same instant and each one asks for
 * a refresh. One rotates the cookie; the rest replay the token it just
 * replaced — and the user is hard-logged-out by their own application.
 *
 * The staff PWA never showed this because it holds one or two queries at a
 * time. The manager console opens six (scope, regions, outlets, users, and
 * whatever the screen itself wants), so it hits it on the first expiry.
 *
 * Module-level rather than store state because it is not state anybody renders,
 * and because it must be shared by every caller including the ones that reach
 * `refresh` through `useAuth.getState()`.
 */
let inFlightRefresh = null;
/**
 * Bumped by `signOut`, so a refresh already in flight can tell that the session
 * it was refreshing no longer exists.
 *
 * Dropping the promise reference alone is not enough: the async function is
 * already running and will still reach its `set` call. Without this, signing
 * out while a refresh was in flight put the user straight back in a moment
 * later.
 */
let sessionEpoch = 0;
/**
 * The client half of the session, shared by every application.
 *
 * ONLY THE ACCESS TOKEN LIVES HERE, on web. The refresh token is an HttpOnly
 * cookie (TAD 4.6) and never enters JavaScript, so an XSS on this page yields a
 * credential that expires in fifteen minutes rather than one that renews itself
 * indefinitely. The native client has no cookie jar worth trusting and holds its
 * refresh token in the platform keystore instead — a weaker position, bought
 * back with device binding and a shorter TTL. See ADR-0007.
 *
 * The token is persisted to sessionStorage on web rather than localStorage: a
 * shared field device should not carry a working session into the next person's
 * tab, and closing the browser should end the session the way users already
 * expect. Which store that is now comes from the platform adapter, because
 * React Native has no sessionStorage and no equivalent "dies with the tab"
 * semantics — the native adapter has to choose deliberately rather than inherit.
 *
 * The storage key is deliberately the same everywhere. The web apps are served
 * from different origins and sessionStorage is origin-scoped, so there is
 * nothing to collide; two different keys would only be a difference someone has
 * to remember.
 */
export const useAuth = create()(persist((set, get) => ({
    accessToken: null,
    user: null,
    landing: null,
    bootstrapped: false,
    signedOutReason: null,
    signIn: (response) => {
        // The login response just set the cookie; leave the note that says so.
        platform().sessionHint?.write(true);
        set({
            accessToken: response.accessToken,
            user: response.user,
            landing: response.landing,
            bootstrapped: true,
            // Whatever ended the last session is now history — leaving it set
            // would carry "your session expired" into the session that replaced
            // it, and on a shared console into someone else's.
            signedOutReason: null,
        });
    },
    invalidateRefresh: () => {
        sessionEpoch += 1;
        inFlightRefresh = null;
    },
    signOut: (reason) => {
        // Invalidate any refresh still in flight, so one that succeeds after
        // this point cannot call `set` and put the user back in. Idempotent, so
        // a caller that already ran `invalidateRefresh` loses nothing by the
        // second bump.
        sessionEpoch += 1;
        inFlightRefresh = null;
        /*
         * Best effort and deliberately not awaited: `signOut` is called
         * synchronously from the API client's 401 path and from render code, and
         * making it async would push that decision into every caller. The app's
         * own sign-out awaits the same clear explicitly; this is the safety net
         * for the involuntary case.
         */
        void platform().refreshCredential?.clear();
        // A sign-out is the one moment the client KNOWS there is no cookie to
        // present, and the note is what spares the next load from asking.
        platform().sessionHint?.write(false);
        // `bootstrapped` stays true: signing out is a known answer, and
        // clearing it would send the app straight back to `bootstrap` to
        // recover the session the user just ended.
        set({
            accessToken: null,
            user: null,
            landing: null,
            bootstrapped: true,
            signedOutReason: reason ?? null,
        });
    },
    /**
     * Refresh the access token, at most once at a time.
     *
     * Concurrent callers share one request and one answer — see
     * `inFlightRefresh` above. Callers do not need to know this: they await
     * `refresh()` and get the same `RefreshOutcome` either way.
     */
    refresh: () => {
        if (inFlightRefresh)
            return inFlightRefresh;
        const epoch = sessionEpoch;
        inFlightRefresh = (async () => {
            /*
             * Web sends nothing: the refresh token is an HttpOnly cookie the
             * browser attaches. Native has no cookie jar worth trusting and
             * carries the token itself, so it goes in the body — and the SERVER
             * decides which is acceptable, from the mode recorded on the token
             * family at login. Presenting a cookie session's token in this body
             * is refused (ADR-0007).
             */
            const carried = platform().refreshCredential;
            let stored = null;
            if (carried) {
                try {
                    stored = await carried.load();
                }
                catch {
                    // The keystore is unreadable — a locked device on iOS, a
                    // Keystore fault on Android. The credential may well still be
                    // there, so this is NOT the moment to declare the session over.
                    return 'UNAVAILABLE';
                }
                // No credential, nothing to refresh with. Not an error — this is
                // the ordinary "never signed in on this device" case, and it must
                // not cost a request.
                if (stored === null)
                    return 'REJECTED';
            }
            let response;
            try {
                response = await fetch(`${getApiBase()}/auth/refresh`, {
                    method: 'POST',
                    credentials: platform().credentials,
                    ...(carried
                        ? {
                            headers: { 'content-type': 'application/json' },
                            body: JSON.stringify({ refreshToken: stored }),
                        }
                        : {}),
                });
            }
            catch {
                /*
                 * THE REQUEST NEVER COMPLETED, WHICH SAYS NOTHING ABOUT THE TOKEN.
                 *
                 * This used to return the same answer as a refusal, and the API
                 * client read that as "the session is over" and signed the user
                 * out. On a handset that is simply wrong: a salesperson walking
                 * into a stockroom loses the connection mid-refresh and gets
                 * thrown back to the login screen with a perfectly good session
                 * still sitting in the keystore.
                 */
                return 'UNAVAILABLE';
            }
            if (!response.ok) {
                /*
                 * ONLY THE SERVER REFUSING THE CREDENTIAL KILLS THE CREDENTIAL.
                 *
                 * This used to clear the keystore on ANY non-2xx, which meant a
                 * 502 from the platform during a deploy, a 503 from a cold start,
                 * or a 429 destroyed a fourteen-day refresh token that was never
                 * in question — and the user had to type their NIK and password
                 * back in to recover from somebody else's rollout.
                 */
                if (response.status !== 401 && response.status !== 403)
                    return 'UNAVAILABLE';
                if (carried)
                    await carried.clear().catch(() => undefined);
                // Written HERE and not on `UNAVAILABLE` above: a 502 says nothing
                // about the cookie, and a note that read it as "gone" would turn a
                // rollout blip into a login form on the next load.
                platform().sessionHint?.write(false);
                return 'REJECTED';
            }
            let body;
            try {
                body = (await response.json());
            }
            catch {
                /*
                 * A 200 whose body did not arrive. The server HAS rotated by now,
                 * so the token in the keystore is dead and there is no way to
                 * learn its successor — keeping it only guarantees that the next
                 * refresh is read as a replay and the whole family is revoked for
                 * suspected theft. An honest sign-out is the better failure.
                 */
                if (carried)
                    await carried.clear().catch(() => undefined);
                return 'REJECTED';
            }
            if (carried) {
                /*
                 * PERSISTED BEFORE ANYTHING ELSE, AND AWAITED.
                 *
                 * The presented token is already dead server-side; this response
                 * holds the only live one. Every line between here and a durable
                 * write is a window in which killing the app strands the device
                 * with a rotated token. The server's rotation grace window is what
                 * covers the part of that gap no client can close; this covers the
                 * rest. The epoch check below deliberately comes after.
                 */
                if (!body.refreshToken) {
                    // A BODY family whose successor did not come back. Same reasoning
                    // as the unreadable body above.
                    await carried.clear().catch(() => undefined);
                    return 'REJECTED';
                }
                /*
                 * CHECKED IMMEDIATELY BEFORE THE WRITE as well as after it. The
                 * check below discards a token that was saved while a sign-out ran,
                 * which is correct but leaves it on the device for the length of one
                 * more keystore round trip. Not writing it in the first place is
                 * strictly better, and this is the last moment at which that is
                 * still an option.
                 */
                if (epoch !== sessionEpoch) {
                    await carried.clear().catch(() => undefined);
                    return 'REJECTED';
                }
                try {
                    await carried.save(body.refreshToken);
                }
                catch {
                    await carried.clear().catch(() => undefined);
                    return 'REJECTED';
                }
            }
            // The user signed out while this was in flight. The new token is
            // real, but nobody asked for it — discard it rather than silently
            // restoring a session they ended.
            if (epoch !== sessionEpoch) {
                if (carried)
                    await carried.clear().catch(() => undefined);
                return 'REJECTED';
            }
            set({ accessToken: body.accessToken, user: body.user, landing: body.landing });
            // After the epoch check, deliberately: a refresh that landed during a
            // sign-out must not overwrite the `false` that sign-out just wrote.
            platform().sessionHint?.write(true);
            return 'REFRESHED';
        })();
        // Cleared only after the promise settles, so a caller arriving during
        // the request joins it rather than starting a second one. Chained
        // rather than `finally` so the returned promise is the settled one.
        void inFlightRefresh.then(() => {
            inFlightRefresh = null;
        }, () => {
            inFlightRefresh = null;
        });
        return inFlightRefresh;
    },
    /**
     * Recover the session on page load, before anything decides to render a
     * login form.
     *
     * The access token lives in sessionStorage and the refresh token lives in
     * an HttpOnly cookie that outlives it by days. So the two credentials
     * disappear at different times, and the gap between them is the bug this
     * exists to close: open the console in a second tab, or reopen the
     * browser, and the tab has no access token while the cookie is still
     * perfectly valid. Without this the app read "no access token" as "not
     * signed in" and asked a signed-in user to sign in again.
     *
     * The cookie cannot be read from JavaScript — that is the point of it —
     * so the only way to find out whether a session exists is to try. A
     * failure here is the ordinary not-signed-in case, not an error: it costs
     * one 401 on the login screen and nothing else — and where the platform
     * keeps a `sessionHint`, only until that answer has been written down.
     * See the check below, and the adapter for why an unwritten hint asks.
     *
     * Concurrent callers are safe. `refresh` is single-flighted, so React's
     * double-invoked effects in development share one request rather than
     * replaying the rotated token.
     */
    bootstrap: async () => {
        /*
         * Hydration is explicit because `skipHydration` turned it off — see the
         * persist options below. This is the right place for it and the only
         * one: `bootstrap` is already the single gate every screen waits on
         * (`use-session-bootstrap.ts` returns false until it resolves, and all
         * three apps render a loading state rather than a login form), so the
         * stored token is back in the store before anything can misread its
         * absence as a signed-out user.
         *
         * Idempotent, and cheap on the second call — which matters because
         * React double-invokes the mount effect in development.
         *
         * KNOWN AND MEASURED: awaiting this does NOT await the write-back.
         * zustand's persist fires `setItem` without awaiting it, so a storage
         * write that rejects surfaces as an unhandled rejection while this
         * promise resolves as though everything worked. Harmless for the access
         * token — it is re-derivable from the refresh endpoint, which is what
         * the rest of this function does. It is NOT harmless for a credential
         * that cannot be re-derived, so when the native client stores its
         * refresh token it must write to the keystore directly and await it,
         * not route it through this persister. See ADR-0007.
         */
        await useAuth.persist.rehydrate();
        if (get().bootstrapped)
            return;
        // This tab already holds a token. Nothing to recover — and spending the
        // single-use refresh token here would rotate a cookie that the API
        // client is going to rotate anyway on the first 401.
        if (get().accessToken !== null) {
            set({ bootstrapped: true });
            return;
        }
        /*
         * The browser was told — by a sign-out, or by the server refusing the
         * last refresh — that there is no cookie to present. Asking anyway
         * would produce exactly the 401 the note exists to avoid.
         *
         * STRICTLY `false`. `null` falls through to the request: it is what a
         * browser that signed in before the note existed reads, and its cookie
         * is as good as anyone's. Native adapters have no hint at all and take
         * the same path — `refresh` reads the keystore and spends no request
         * when it is empty.
         */
        if (platform().sessionHint?.read() === false) {
            set({ bootstrapped: true });
            return;
        }
        await get().refresh();
        set({ bootstrapped: true });
    },
}), {
    name: 'fv-session',
    /*
     * Only the credential is persisted. `bootstrapped` must be false on every
     * load — persisting it would restore "we already checked" into a tab that
     * has not checked anything, which is exactly the state this whole
     * mechanism exists to avoid.
     */
    partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
        landing: state.landing,
    }),
    /*
     * HYDRATION IS DEFERRED, and this is not a tuning knob.
     *
     * zustand reads storage when the store is CREATED, which here is when the
     * module is imported — before the app has run a line of its own code, and
     * therefore before `configurePlatform` has been called. On web that was
     * survivable because `sessionStorage` is a global that always exists. On
     * React Native there is no such global and no synchronous store at all:
     * `expo-secure-store` returns promises, so an eager read cannot work even
     * in principle, and merely importing this module would throw.
     *
     * So hydration moves to the first line of `bootstrap()` above, which every
     * screen already waits on. The cost is that `useAuth.getState()` is empty
     * between import and bootstrap — which was already true of a cold tab, and
     * is exactly the state `bootstrapped: false` exists to describe.
     */
    skipHydration: true,
    storage: {
        getItem: async (name) => {
            const value = await platform().storage.getItem(name);
            return value ? JSON.parse(value) : null;
        },
        setItem: async (name, value) => {
            await platform().storage.setItem(name, JSON.stringify(value));
        },
        removeItem: async (name) => {
            await platform().storage.removeItem(name);
        },
    },
}));
//# sourceMappingURL=auth.store.js.map